import { ChangeEvent, Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useLang } from 'hooks/lang';

import { calculateServiceCost, ProjectService, TeamServiceWithTeam } from 'entities';

import { Collapse, Column, Row, Card, IconButton, LabeledCheckbox } from 'components/Blocks';
import { VariableCostInput } from 'components/Inputs';
import { ChevronLeftIcon, ChevronRightIcon } from 'components/Icons';

import { useProjectProfile } from '../../context';
import { useKeyPress, useToggle, useWatch } from 'hooks';

import { getCountryLabel } from 'utils/countries';
import { getLastIndex } from 'utils/array';
import { formatCurrency } from 'utils/text';

interface CostsProps {
  serviceSelected: TeamServiceWithTeam | undefined;
  service: Omit<ProjectService, 'id' | 'name' | 'costCalculation'> | undefined;
  setService: Dispatch<SetStateAction<Omit<ProjectService, 'id'>>>;
}

export function ServiceCosts({ serviceSelected, service, setService }: CostsProps) {
  const { project } = useProjectProfile();

  const [countryIndex, setCountryIndex] = useState(0);

  const { isOn: costAlreadyIncluded, toggle: toggleCostAlreadyIncluded } = useToggle();

  const { t } = useLang();

  const handleIncludedCheckboxChange = useCallback(
    ({ target }: { target: { checked: boolean } }) => {
      const { checked } = target;

      setService((old) => ({
        ...old,
        costs: Object.fromEntries(
          Object.entries(old.costs).map(([country, costVariables]) => [
            country,
            costVariables.map((cost) => ({
              ...cost,
              value: checked ? '0' : '',
              ...(cost.type === 'CURRENCY' ? { currency: project.currency } : {}),
            })),
          ]),
        ),
      }));

      toggleCostAlreadyIncluded();
    },
    [setService, toggleCostAlreadyIncluded, project.currency],
  );

  useWatch(service?.outsourceConfig, (config) => {
    if (config && costAlreadyIncluded) {
      handleIncludedCheckboxChange({ target: { checked: false } });
    }
  });

  const updateServiceCost = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
      const { name, value } = target;

      const [country, index] = name.split('#');

      setService((old) => ({
        ...old,
        costs: {
          ...old.costs,
          [country]: old.costs[country].map((cost, costIndex) =>
            costIndex === Number(index)
              ? {
                  ...cost,
                  value: value as string,
                  ...(cost.type === 'CURRENCY' ? { currency: project.currency } : {}),
                }
              : cost,
          ),
        },
      }));
    },
    [setService, project.currency],
  );

  const nextCountry = () => {
    setCountryIndex((old) => {
      const newIndex = old + 1;

      return project.countries[newIndex] ? newIndex : 0;
    });
  };

  const previousCountry = () => {
    setCountryIndex((old) => {
      const newIndex = old - 1;

      return project.countries[newIndex] ? newIndex : getLastIndex(project.countries);
    });
  };

  const country = project.countries[countryIndex];

  const multiCountry = project.countries.length > 1;

  useKeyPress('ArrowRight', () => multiCountry && nextCountry());
  useKeyPress('ArrowLeft', () => multiCountry && previousCountry());

  return (
    <Column>
      <Row gap="2" justifyContent="space-between">
        <span className="font-semibold">{t('cost')}</span>

        {serviceSelected && !service?.outsourceConfig && (
          <LabeledCheckbox
            containerClassName="ml-auto"
            checked={costAlreadyIncluded}
            onChange={handleIncludedCheckboxChange}
            label={t('already-included')}
            labelSide="left"
          />
        )}
      </Row>

      <Collapse expanded={!costAlreadyIncluded}>
        {!serviceSelected ? (
          <span className="italic">{t('select-a-service-to-enable-costs')}</span>
        ) : (
          <Card flow="column">
            <Row
              justifyContent="space-between"
              className="rounded-md bg-muted px-2 py-1 font-medium text-primary"
            >
              {multiCountry && (
                <IconButton size="small" onClick={previousCountry} tooltipContent="Press ←">
                  <ChevronLeftIcon />
                </IconButton>
              )}

              {getCountryLabel(country)}

              {multiCountry && (
                <IconButton size="small" onClick={nextCountry} tooltipContent="Press →">
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Row>

            <div className="grid grid-cols-auto-150 gap-4">
              {serviceSelected.costVariables.map((cost, index) => (
                <VariableCostInput
                  currency={project.currency}
                  currencyDisabled
                  onChange={updateServiceCost}
                  value={service?.costs?.[country][index].value || ''}
                  name={`${country}#${index}`}
                  key={`${country}#${cost.id}`}
                  type={cost.type}
                  label={cost.name}
                />
              ))}
            </div>

            <Column gap="2">
              {multiCountry && (
                <Row justifyContent="space-between">
                  <span>Total {t('country')}</span>
                  <span className="font-semibold text-primary">
                    {formatCurrency(
                      calculateServiceCost({
                        ...serviceSelected,
                        ...service,
                        costs: { [country]: service?.costs[country] ?? {} },
                      } as ProjectService),
                      project.currency,
                    )}
                  </span>
                </Row>
              )}

              <Row justifyContent="space-between">
                <span>Total</span>
                <span className="font-semibold text-primary">
                  {formatCurrency(
                    calculateServiceCost({ ...serviceSelected, ...service } as ProjectService),
                    project.currency,
                  )}
                </span>
              </Row>
            </Column>
          </Card>
        )}
      </Collapse>
    </Column>
  );
}
